import { useState, useCallback } from 'react';
import { window } from '../util/globals';

const STORAGE_KEY_PREFIX = '@@lifeomic/store/';

export const getPrefixedStorageKey = (itemKey: string) =>
  STORAGE_KEY_PREFIX + itemKey;

export const safeGetPersistedValue = <TValue>(
  storageKey: string,
): TValue | null => {
  try {
    const storedValue = window.localStorage.getItem(
      getPrefixedStorageKey(storageKey),
    );

    if (!storedValue) return null;

    return (JSON.parse(storedValue) as TValue) || null;
  } catch (e) {
    console.error(
      `Error occurred retrieving the useLocalStorageState local storage item with key "${storageKey}"`,
      e,
    );
    return null;
  }
};

export const safePersistValue = <TValue>(storageKey: string, value: TValue) => {
  try {
    const storedValue = JSON.stringify(value);
    window.localStorage.setItem(getPrefixedStorageKey(storageKey), storedValue);
  } catch (e) {
    console.error(
      `Error occurred trying to persist the useLocalStorageState item to local storage with key "${storageKey}"`,
      e,
    );
  }
};

const getDefaultStateValue = <TValue>(
  storageKey: string,
  defaultValue: TValue,
): TValue | null => {
  const valueLoadedFromLocalStorage = safeGetPersistedValue<TValue>(storageKey);
  return valueLoadedFromLocalStorage === null
    ? defaultValue
    : valueLoadedFromLocalStorage;
};

/**
 * A hook with an identical interface to the React useState except the state value attempts to initialize from
 * local storage.  Every state update is persisted to local to load.
 *
 * It's important to not rely on the local storage value for application-critical data or sensitive information.
 * Data can be cleared and can be accessible from anyone using the browser.
 * @param storageKey - A unique identifier to associate to a local storage value
 * @param defaultValue - When no value is found in local storage, this is used
 */
const useLocalStorageState = <TValue = any>(
  storageKey: string,
  defaultValue: TValue,
): [TValue, (updatedState: TValue) => void] => {
  const [state, setState] = useState(() =>
    getDefaultStateValue<TValue>(storageKey, defaultValue),
  );

  const interceptSetState = useCallback(
    (updatedState: TValue) => {
      safePersistValue(storageKey, updatedState);
      setState(updatedState);
    },
    [setState, storageKey],
  );

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return [state, interceptSetState];
};

export default useLocalStorageState;
